import React from 'react';
import { Corner, Root } from '@radix-ui/react-scroll-area';
import clsx from 'clsx';
import type { VirtuosoGridHandle } from 'react-virtuoso';
import { VirtuosoGrid } from 'react-virtuoso';
import { getManyFromList } from '@soundxyz/utils/helpers';
import { LoadingGIF } from '@components/basics/LoadingGif';
import {
  ScrollViewport,
  StyledScrollbar,
  StyledThumb,
} from '@components/main-components/containers/RadixScroll';
import { StyledTabList, Tabs, TabTrigger } from '@components/main-components/tabs/Tab';
import { useAuth } from '@context/auth/base';
import { useMediaQuery } from '@context/mediaQuery';
import { ParamsStore, useParams } from '@context/params';
import { type SearchQuery } from '@graphql/reactQuery';
import { globalSearchSoundState, useSearchInputNew } from '@hooks/search/useSearchInputNew';
import { useSafeLayoutEffect } from '@hooks/useSafeLayoutEffect';
import { useAudioController } from '@store/audio/AudioController';
import { GRID_STYLE } from '@utils/constants';
import { getNextTrackIds } from '@utils/helpers';
import { AllResults } from './AllResults/AllResults';
import { AllResultsMobileSkeleton } from './AllResults/AllResultsMobileSkeleton';
import { Filters } from './Filters';
import { MobileFilters } from './MobileFilters';
import { NoResultsExplorePage } from './NoResultsExplorePage';
import { RecentSearches } from './RecentSearches';
import {
  ArtistSearchCard,
  CollectorSearchCard,
  PlaylistSearchCard,
  ReleaseSearchCard,
} from './SearchCards';
import { SearchResultsSkeleton } from './SearchResultsSkeleton';
import { ArtistsSort, CollectorsSort, PlaylistsSort, SoundsSort } from './Sort';
import { resetFilters, useCurrentSearchTabsAndFilters } from './helpers';
import { isSearchTab, SEARCH_TAB_CHOICES } from './schema';
import { setActiveTab, setDefaultSort, useSearchPageStore } from './useSearchPageStore';
import { useSearchResults } from './useSearchResults';

const getSortDropdown = ({ paramActiveTab }: { paramActiveTab: string | null }) => {
  switch (paramActiveTab) {
    case SEARCH_TAB_CHOICES.SOUNDS:
      return <SoundsSort />;
    case SEARCH_TAB_CHOICES.ARTISTS:
      return <ArtistsSort />;
    case SEARCH_TAB_CHOICES.COLLECTORS:
      return <CollectorsSort />;
    case SEARCH_TAB_CHOICES.PLAYLISTS:
      return <PlaylistsSort />;
    default:
      return null;
  }
};

export const renderItemContent = ({
  item,
  getNextTrackIds,
}: {
  item: NonNullable<
    NonNullable<
      NonNullable<SearchQuery['search']>[
        | 'releasesPaginated'
        | 'artistsPaginated'
        | 'collectors'
        | 'shelves']
    >['edges'][number]
  >['node'];
  getNextTrackIds?: () => string[];
}) => {
  if (item.__typename === 'Release') {
    return <ReleaseSearchCard data={item} getNextTrackIds={getNextTrackIds} rowInMobile />;
  }

  if (item.__typename === 'Artist') {
    return <ArtistSearchCard artist={item} rowInMobile />;
  }

  if (item.__typename === 'User') {
    return <CollectorSearchCard collector={item} rowInMobile />;
  }

  if (item.__typename === 'Shelf') {
    return <PlaylistSearchCard data={item} rowInMobile />;
  }

  return null;
};

export const SearchResults = ({ onTouchStart }: { onTouchStart: (() => void) | undefined }) => {
  const { authUser } = useAuth();
  const authUserId = authUser?.id;
  const { tab: paramActiveTab, q } = useParams();
  const searchPageStore = useSearchPageStore();
  const activeTab = searchPageStore.activeTab;

  const { showAvailabilityFilter, showLocationFilter, showGenreFilter } =
    useCurrentSearchTabsAndFilters();

  const {
    searchResults,
    loadMore,
    isInitialLoading: isLoading,
    isFetchingNextPage,
    hasNextPage = false,
  } = useSearchResults();

  const { barInputText, searchPageText } = useSearchInputNew({
    searchState: globalSearchSoundState,
  });

  const { hideAudioPlayer, activeTrackId } = useAudioController();
  const isAudioPlayerHidden = hideAudioPlayer || !activeTrackId;

  const { tablet } = useMediaQuery();

  const loadingNext = isFetchingNextPage && hasNextPage;

  const memoizedFooter = React.useMemo(() => (loadingNext ? <LoadingGIF /> : null), [loadingNext]);

  const gridRef = React.useRef<VirtuosoGridHandle>(null);

  const isSearchInputEmpty = q === '' || q === null;
  const hasNoSearchResults = searchResults.length === 0 && !isLoading;

  // When tab query changes, update local state powering the tab component
  useSafeLayoutEffect(() => {
    if (tablet) {
      if (isSearchTab(paramActiveTab)) {
        return setActiveTab({
          activeTab: paramActiveTab,
        });
      }
    }
  }, [tablet, paramActiveTab]);

  useSafeLayoutEffect(() => {
    if (tablet && searchPageText !== '') {
      if (paramActiveTab == null) {
        return setActiveTab({ activeTab: SEARCH_TAB_CHOICES.ALL });
      }
    }
  }, [tablet, searchPageText]);

  const sortDropdown = getSortDropdown({ paramActiveTab });

  return (
    <div className="mb-12 md:mx-auto lg:max-w-7xl" onTouchStart={onTouchStart}>
      {isSearchInputEmpty && <RecentSearches />}

      {/* Search category tabs */}
      <Tabs
        value={activeTab}
        onValueChange={v => {
          ParamsStore.tab = v;
          resetFilters();
        }}
      >
        <Root className="overflow-hidden px-4 md:px-6">
          <ScrollViewport>
            <StyledTabList>
              {Object.values(SEARCH_TAB_CHOICES).map(tab => {
                if (tab === 'all' && isSearchInputEmpty) return null;

                return (
                  <TabTrigger
                    key={tab}
                    value={tab}
                    label={tab}
                    onClick={() => {
                      setActiveTab({ activeTab: tab });
                      setDefaultSort({ type: tab });
                    }}
                  />
                );
              })}
            </StyledTabList>
            <StyledScrollbar hiddenOnMobile orientation="horizontal">
              <StyledThumb hiddenOnMobile />
            </StyledScrollbar>
            <Corner />
          </ScrollViewport>
          {activeTab !== SEARCH_TAB_CHOICES.ALL && (
            <div className="mt-2 flex items-center justify-between md:mt-0 md:hidden">
              <MobileFilters
                showAvailabilityFilter={showAvailabilityFilter}
                showLocationFilter={showLocationFilter}
                showGenreFilter={showGenreFilter}
              />
              {sortDropdown}
            </div>
          )}
        </Root>
      </Tabs>

      {activeTab === SEARCH_TAB_CHOICES.ALL ? (
        <AllResults
          searchInput={tablet ? searchPageText : barInputText}
          isAudioPlayerHidden={isAudioPlayerHidden}
        />
      ) : (
        <div
          className={clsx(
            'mx-auto my-0 flex min-h-screen flex-col px-4 md:gap-4',
            'md:gap-2 md:px-6 md:pb-4',
            'lg: max-w-7xl',
            loadingNext ? 'pb-28 md:pb-40' : 'pb-2 md:pb-4',
          )}
        >
          <div key={`search-results-${authUserId}}`}>
            <div className="mt-4 flex hidden justify-between md:flex">
              <Filters
                showAvailabilityFilter={showAvailabilityFilter}
                showLocationFilter={showLocationFilter}
                showGenreFilter={showGenreFilter}
              />
              {sortDropdown}
            </div>
            {hasNoSearchResults && !isLoading && (
              <NoResultsExplorePage searchInput={searchPageText} />
            )}
            {/* Show search results in a VirtuosoGrid */}
            {/* Key is needed to avoid virtuoso not fetching new pages when user logs in or filters change
                This is Virtuoso Grid specific. Virtuoso List does not have this issue */}
            <div className="mt-2 pb-6 md:mt-4 md:pb-0" key={`search-results-${authUserId}}`}>
              <VirtuosoGrid
                ref={gridRef}
                context={{ loadMore }}
                endReached={loadMore}
                data={searchResults}
                useWindowScroll
                itemContent={(_, item) => {
                  return renderItemContent({
                    item,
                    getNextTrackIds:
                      item.__typename === 'Release'
                        ? getNextTrackIds({
                            trackId: item.track.id,
                            releases: getManyFromList(searchResults, edge => {
                              return edge.__typename === 'Release' ? edge : null;
                            }),
                          })
                        : undefined,
                  });
                }}
                overscan={100}
                listClassName={clsx('gap-4', GRID_STYLE)}
                components={{ Footer: () => memoizedFooter }}
              />
            </div>
            {isLoading && (
              <div className="mt-2">
                <div className="hidden md:block">
                  <SearchResultsSkeleton />
                </div>
                <div className="block md:hidden">
                  <AllResultsMobileSkeleton />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
